import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Modal,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { ListAgreementTermResponse } from 'api/agreement-term/api.types';
import CardHistoricoTermoAdesao from 'components/termos/CardHistoricoTermoAdesao';
import React, { useState } from 'react';
import { Colors } from 'theme';
import DocumentComponent from './document_component';

interface Ata {
  titlePDF?: string;
  label?: string;
  fileName?: string;
  fileSize?: string;
  base64?: string;
  onView?: () => void;
}

interface Beneficiary {
  name: string;
  cpf: string;
}

interface Sub {
  data?: {
    title?: string;
    label?: string;
    value?: string;
  }[];
  ata?: Ata[];
}

interface Product {
  title?: string;
  data?: {
    name?: string;
    cnpjOrcpf?: string;
    value?: string;
  }[];
}

interface ArchiveData {
  base64: string;
  name: string;
  size: number;
  type: string;
}
interface DataConfirmationProps {
  title?: string;
  subtitle?: string;
  data?: {
    label: string;
    value: string;
    isEditable?: boolean;
    onAction?: () => void;
  }[];
  subData?: Sub[];
  dataProduct?: Product[];
  additionalText?: string;
  atas?: Ata[];
  beneficiaries?: Beneficiary[];
  titleBeneficiaries?: string;
  showEdit?: true;
  historyTerms?: ListAgreementTermResponse[];
  showHistoryTerms?: boolean;
  showTerm?: boolean;
  document_uuid?: string | null;
  document_show?: boolean;
}

const DataConfirmation: React.FC<DataConfirmationProps> = ({
  title,
  subtitle,
  data,
  subData,
  dataProduct,
  atas,
  beneficiaries,
  titleBeneficiaries,
  showEdit,
  historyTerms,
  showHistoryTerms,
  showTerm,
  document_uuid,
  document_show = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<Ata | null>(null);
  const [selectedHistoryTerms, setSelectedHistoryTerms] = useState<
    ListAgreementTermResponse[]
  >([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleOpenModal = async (ata: Ata) => {
    console.log(ata);
    setSelectedFile(ata);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedFile(null);
  };

  const totalPages = beneficiaries
    ? Math.ceil(beneficiaries.length / itemsPerPage)
    : 0;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedHistoryTerms(historyTerms!);
    } else {
      setSelectedHistoryTerms([]);
    }
  };

  const handleSelectTerm = (term: ListAgreementTermResponse) => {
    if (selectedHistoryTerms.includes(term)) {
      setSelectedHistoryTerms(selectedHistoryTerms.filter((t) => t !== term));
    } else {
      setSelectedHistoryTerms([...selectedHistoryTerms, term]);
    }
  };

  return (
    <Stack
      sx={{
        backgroundColor: Colors.BRANCO_GOV,
        borderRadius: '8px',
        marginTop: '1rem',
        padding: '1rem',
        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
      }}
    >
      {/* Title Section */}
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        onClick={toggleExpanded}
        sx={{
          cursor: 'pointer',
          padding: '0.5rem 1rem',
          borderBottom: isExpanded ? `1px solid ${Colors.CINZA_CLARO}` : 'none',
        }}
      >
        <Stack direction='row' alignItems='center' spacing={1}>
          <IconButton size='small' sx={{ marginRight: '0.5rem' }}>
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <Typography variant='h6' fontWeight='500' fontSize='1.25rem'>
            {title}
          </Typography>
        </Stack>
        {showEdit && (
          <Button
            variant='text'
            color='primary'
            onClick={(e) => e.stopPropagation()}
            sx={{ textTransform: 'none' }}
          >
            Editar
          </Button>
        )}
      </Stack>

      {/* Content Section */}
      {isExpanded && (
        <>
          {data && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '1rem',
                padding: '1rem',
              }}
            >
              {data?.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    flex: '1 1 calc(50% - 1rem)',
                    minWidth: '200px',
                  }}
                >
                  <Typography
                    variant='body1'
                    fontWeight={400}
                    sx={{ color: Colors.CINZA_ESCURO, marginBottom: '0.5rem' }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    variant='body2'
                    fontWeight={400}
                    sx={{ color: Colors.CINZA_MEDIO }}
                  >
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Data Product */}
          {dataProduct &&
            dataProduct.map((place, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                  padding: '1rem',
                }}
              >
                {/* Título da seção */}
                <Typography
                  variant='body1'
                  fontWeight={600}
                  sx={{
                    color: Colors.CINZA_ESCURO,
                    fontSize: 16,
                    borderBottom: '1px solid #ccc',
                    paddingBottom: '8px',
                  }}
                >
                  {place.title}
                </Typography>

                {/* Listagem dos produtos */}
                {place?.data?.map((item, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      paddingY: '8px',
                      borderBottom: '1px solid #ccc',
                    }}
                  >
                    {/* Nome/Razão Social */}
                    <Box sx={{ flex: 2 }}>
                      <Typography
                        variant='body2'
                        fontWeight={600}
                        sx={{ color: Colors.CINZA_ESCURO, fontSize: 14 }}
                      >
                        Nome/ Razão Social:
                      </Typography>
                      <Typography
                        variant='body2'
                        fontWeight={600} // Deixa o nome em negrito como na imagem
                        sx={{ color: Colors.CINZA_MEDIO, fontSize: 14 }}
                      >
                        {item.name}
                      </Typography>
                    </Box>

                    {/* CNPJ/CPF */}
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant='body2'
                        fontWeight={600}
                        sx={{ color: Colors.CINZA_ESCURO, fontSize: 14 }}
                      >
                        CNPJ:
                      </Typography>
                      <Typography
                        variant='body2'
                        fontWeight={400}
                        sx={{ color: Colors.CINZA_MEDIO, fontSize: 14 }}
                      >
                        {item.cnpjOrcpf}
                      </Typography>
                    </Box>

                    {/* Valor */}
                    <Box sx={{ flex: 1, textAlign: 'right' }}>
                      <Typography
                        variant='body2'
                        fontWeight={600}
                        sx={{ color: Colors.CINZA_ESCURO, fontSize: 14 }}
                      >
                        Valor:
                      </Typography>
                      <Typography
                        variant='body2'
                        fontWeight={600} // Deixa o valor em negrito como na imagem
                        sx={{ color: Colors.CINZA_MEDIO, fontSize: 14 }}
                      >
                        {item.value}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            ))}

          {/* Data Large */}
          {subData && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '1rem',
                padding: '1rem',
              }}
            >
              {subData.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    flex: '1 1 calc(100% - 1rem)',
                    minWidth: '200px',
                  }}
                >
                  {item.data?.map((place, index) => (
                    <>
                      <Typography
                        variant='body1'
                        fontWeight={600}
                        sx={{
                          color: Colors.CINZA_MEDIO,
                          fontSize: 18,
                          marginBottom: '0.5rem',
                        }}
                      >
                        {place.title}
                      </Typography>
                      <Typography
                        variant='body1'
                        fontWeight={400}
                        sx={{
                          color: Colors.CINZA_ESCURO,
                          marginBottom: '0.5rem',
                        }}
                      >
                        {place.label}
                      </Typography>
                      <Typography
                        variant='body2'
                        fontWeight={400}
                        sx={{ color: Colors.CINZA_MEDIO }}
                      >
                        {place.value}
                      </Typography>
                    </>
                  ))}
                  {item.ata?.map((ata, index) => (
                    <>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          borderRadius: '4px',
                        }}
                      >
                        <Typography
                          variant='body2'
                          sx={{
                            color: Colors.CINZA_MEDIO,
                            fontWeight: '700',
                            marginTop: '1rem',
                            marginBottom: '0.5rem',
                          }}
                        >
                          {ata.label}
                        </Typography>
                        <Button
                          variant='text'
                          color='primary'
                          startIcon={<OpenInNewIcon />}
                          onClick={() => handleOpenModal(ata)}
                        >
                          Visualizar
                        </Button>
                      </Box>
                      <Stack
                        key={index}
                        direction='row'
                        justifyContent='space-between'
                        alignItems='center'
                        padding='0.5rem'
                        sx={{
                          backgroundColor: Colors.CINZA_CLARO,
                          borderRadius: '4px',
                        }}
                      >
                        <Box display='flex' alignItems='center' gap='0.5rem'>
                          <PictureAsPdfIcon
                            sx={{
                              color: Colors.CINZA_ESCURO,
                              fontSize: '24px',
                            }}
                          />
                          <Box>
                            <Typography
                              variant='body2'
                              fontWeight={400}
                              sx={{ color: Colors.CINZA_MEDIO }}
                            >
                              {ata.label}
                            </Typography>
                            <Typography
                              variant='body2'
                              sx={{ color: Colors.CINZA_ESCURO }}
                            >
                              {ata.fileName} ({ata.fileSize})
                            </Typography>
                          </Box>
                        </Box>
                      </Stack>
                    </>
                  ))}
                </Box>
              ))}
            </Box>
          )}

          {/* Termo leitura */}
          {showTerm && (
            <Box sx={{ marginTop: '1rem' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingBottom: '10px',
                  borderBottom: '1px solid #E0E0E0',
                }}
              >
                <Typography
                  variant='h6'
                  fontWeight='bold'
                  sx={{
                    display: 'flex',
                    textAlign: 'center',
                  }}
                >
                  TERMO DE ADESÃO MUNICIPAL
                </Typography>
              </Box>

              <Box sx={{ marginTop: '1rem', textAlign: 'justify' }}>
                <Typography variant='body1'>
                  Eu assumo o compromisso de manter confidencialidade e sigilo
                  sobre todas as informações a que tiver acesso como membro da
                  Comissão de Acolhimento e Orientação da SEAF, instaurada nos
                  termos da Portaria Interna SEAF, de 10 de outubro de 2016.
                </Typography>

                <Typography variant='body1' sx={{ marginTop: '1rem' }}>
                  Por este termo de confidencialidade e sigilo comprometo-me:
                </Typography>

                <Typography variant='body1' sx={{ marginTop: '0.5rem' }}>
                  1. A não utilizar as informações confidenciais a que tiver
                  acesso, para gerar benefício próprio exclusivo e/ou
                  unilateral, presente ou futuro, ou para o uso de terceiros;
                </Typography>

                <Typography variant='body1' sx={{ marginTop: '0.5rem' }}>
                  2. A não efetuar nenhuma gravação ou cópia da documentação
                  confidencial a que tiver acesso;
                </Typography>

                <Typography variant='body1' sx={{ marginTop: '0.5rem' }}>
                  3. A não me apropriar de material confidencial e/ou sigiloso
                  que venha a ser disponibilizado;
                </Typography>

                <Typography variant='body1' sx={{ marginTop: '0.5rem' }}>
                  4. A não repassar o conhecimento das informações
                  confidenciais, responsabilizando-me por todas as pessoas que
                  vierem a ter acesso às informações, por meu intermédio, e
                  obrigando-me, assim, a ressarcir a ocorrência de qualquer dano
                  e/ou prejuízo oriundo de uma eventual quebra de sigilo das
                  informações fornecidas.
                </Typography>

                <Typography variant='body1' sx={{ marginTop: '1rem' }}>
                  Neste Termo, as seguintes expressões serão assim definidas:
                  Informação Confidencial significará toda informação revelada
                  sob a forma escrita, verbal ou por quaisquer outros meios.
                </Typography>

                <Typography variant='body1' sx={{ marginTop: '0.5rem' }}>
                  Informação Confidencial inclui, mas não se limita, à
                  informação sobre as questões relativas ao propósito da criação
                  da Comissão de Acolhimento e Orientação, de acordo com a
                  Portaria Interna SEAF, de 10 de outubro de 2016.
                </Typography>
              </Box>
            </Box>
          )}

          {/* Histórico de Termos */}
          {showHistoryTerms && historyTerms && (
            <Box sx={{ marginTop: '1rem' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingBottom: '10px',
                  borderBottom: '1px solid #E0E0E0',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Button
                    variant='outlined'
                    endIcon={<ArrowDropDownIcon />}
                    sx={{
                      color: '#0048B6',
                      border: '1px solid #0048B6',
                      borderRadius: '16px',
                      paddingX: '16px',
                    }}
                  >
                    Ordenar
                  </Button>
                  <Button
                    variant='outlined'
                    endIcon={<ArrowDropDownIcon />}
                    sx={{
                      color: '#0048B6',
                      border: '1px solid #0048B6',
                      borderRadius: '16px',
                      paddingX: '16px',
                    }}
                  >
                    Filtrar
                  </Button>
                </Box>
              </Box>
              <Box sx={{ width: '100%' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    maxWidth: '1200px',
                    margin: '0 auto',
                    height: '50px',
                  }}
                >
                  <Checkbox
                    sx={{ color: '#000000' }}
                    checked={
                      selectedHistoryTerms.length === historyTerms.length
                    }
                    onChange={handleSelectAll}
                  />
                  <Typography sx={{ color: '#000000', fontSize: '18px' }}>
                    Selecionar todos | {selectedHistoryTerms.length} de{' '}
                    {historyTerms.length} selecionado(s)
                  </Typography>
                </Box>
              </Box>
              {historyTerms.map((term, index) => (
                <CardHistoricoTermoAdesao
                  key={index}
                  term={term}
                  index={index}
                  selectedHistoryTerms={selectedHistoryTerms}
                  handleSelectTerm={handleSelectTerm}
                />
              ))}
            </Box>
          )}

          {/* ATAS Section */}
          {atas && (
            <Stack spacing={2} sx={{ marginTop: '1rem' }}>
              {atas.map((ata, index) => (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderRadius: '4px',
                    }}
                  >
                    <Typography
                      variant='body2'
                      sx={{
                        color: Colors.CINZA_MEDIO,
                        fontWeight: '700',
                        marginTop: '1rem',
                        marginBottom: '0.5rem',
                      }}
                    >
                      {ata.label} ({ata.fileSize})
                    </Typography>
                    <Button
                      variant='text'
                      color='primary'
                      startIcon={<OpenInNewIcon />}
                      onClick={() => handleOpenModal(ata)}
                    >
                      Visualizar
                    </Button>
                  </Box>
                  <Stack
                    key={index}
                    direction='row'
                    justifyContent='space-between'
                    alignItems='center'
                    padding='0.5rem'
                    sx={{
                      backgroundColor: Colors.CINZA_CLARO,
                      borderRadius: '4px',
                    }}
                  >
                    <Box>
                      <Typography
                        variant='body2'
                        fontWeight={400}
                        sx={{ color: Colors.CINZA_MEDIO }}
                      >
                        {ata.label}
                      </Typography>
                      <Typography
                        variant='body2'
                        sx={{ color: Colors.CINZA_ESCURO }}
                      >
                        {ata.fileName} ({ata.fileSize})
                      </Typography>
                    </Box>
                  </Stack>
                </>
              ))}

              {/* Beneficiaries Section */}
              {beneficiaries && (
                <>
                  <Typography
                    variant='h6'
                    sx={{ marginTop: '1.5rem', marginBottom: '0.5rem' }}
                  >
                    {titleBeneficiaries}
                  </Typography>
                  {beneficiaries
                    .slice(
                      (currentPage - 1) * itemsPerPage,
                      currentPage * itemsPerPage,
                    )
                    .map((beneficiary, index) => (
                      <Stack
                        key={index}
                        direction='row'
                        justifyContent='space-between'
                        alignItems='center'
                        padding='1rem'
                        border='1px solid #ddd'
                        borderRadius='8px'
                      >
                        <Box>
                          <Typography variant='body1'>
                            {beneficiary.name}
                          </Typography>
                          <Typography variant='body2' color='textSecondary'>
                            CPF: {beneficiary.cpf}
                          </Typography>
                        </Box>
                        <Button
                          variant='outlined'
                          color='primary'
                          onClick={() =>
                            console.log(
                              `Visualizar beneficiário: ${beneficiary.name}`,
                            )
                          }
                          sx={{
                            textTransform: 'none',
                            borderRadius: '8px',
                            fontWeight: 'bold',
                          }}
                        >
                          Visualizar
                        </Button>
                      </Stack>
                    ))}

                  {/* Pagination */}
                  <Stack
                    direction='row'
                    justifyContent='center'
                    spacing={2}
                    marginTop='1rem'
                  >
                    {[...Array(totalPages)].map((_, index) => (
                      <Button
                        key={index}
                        variant={
                          index + 1 === currentPage ? 'contained' : 'outlined'
                        }
                        onClick={() => handlePageChange(index + 1)}
                      >
                        {index + 1}
                      </Button>
                    ))}
                  </Stack>
                </>
              )}
            </Stack>
          )}

          {
            document_show && (
              <DocumentComponent
                uuid={document_uuid}
                podeSubstituir={false}
                podeEnviar={false}
              />
            )
          }
        </>
      )}

      {/* Modal para Visualizar Arquivo */}
      <Modal open={openModal} onClose={handleCloseModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '80%',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: '8px',
            p: 3,
          }}
        >
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            mb={2}
          >
            <Typography variant='h6'>Visualizar Documento</Typography>
            <IconButton onClick={handleCloseModal}>
              <CloseIcon />
            </IconButton>
          </Box>

          {selectedFile && (
            <Paper
              sx={{
                width: '100%',
                height: '500px',
                overflow: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <embed
                src={`data:application/pdf;base64,${selectedFile?.base64}`}
                type='application/pdf'
                width='100%'
                height='100%'
              />
            </Paper>
          )}
        </Box>
      </Modal>
    </Stack>
  );
};

export default DataConfirmation;
